import React, { useState, useRef, useEffect } from 'react';
import './ImageUpload.css';

const ImageUpload = ({ currentImage, onImageChange, label = "Imagem da capa" }) => {
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const pasteAreaRef = useRef(null);

  useEffect(() => {
    if (currentImage) {
      setPreviewUrl(currentImage);
    }
  }, [currentImage]);

  const handleFile = (file) => {
    if (!file) return;

    // Validar se é uma imagem
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem');
      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 5MB');
      return;
    }

    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    // Chamar callback com o arquivo
    onImageChange(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        handleFile(file);
        e.preventDefault();
        break;
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onImageChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="image-upload-container">
      <label className="image-upload-label">{label}</label>
      
      <div
        ref={pasteAreaRef}
        className={`image-upload-area ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onPaste={handlePaste}
        onClick={handleClick}
        tabIndex={0}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />

        {previewUrl ? (
          <div className="image-preview-container">
            <img 
              src={previewUrl} 
              alt="Preview" 
              className="image-preview"
            />
            <button
              type="button"
              className="image-remove-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
            >
              ✕ Remover
            </button>
          </div>
        ) : (
          <div className="image-upload-placeholder">
            <div className="upload-icon">📸</div>
            <div className="upload-text">
              <p className="upload-text-main">
                Clique para selecionar ou arraste uma imagem
              </p>
              <p className="upload-text-secondary">
                Você também pode colar (Ctrl/Cmd + V) uma imagem da área de transferência
              </p>
              <p className="upload-text-hint">
                PNG, JPG ou GIF • Máximo 5MB
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="image-upload-tips">
        <p><strong>💡 Dicas:</strong></p>
        <ul>
          <li><strong>Clique</strong> na área acima para selecionar um arquivo</li>
          <li><strong>Arraste</strong> uma imagem para a área</li>
          <li><strong>Cole</strong> (Ctrl/Cmd + V) uma imagem copiada</li>
          <li>Tamanho recomendado: 1200x630px para melhor visualização</li>
        </ul>
      </div>
    </div>
  );
};

export default ImageUpload;
